/*
 * Popup "Compre também" (order bump) — snippets/order-bump.liquid.
 *
 * Quando o tema adiciona um produto ao carrinho ele dispara `theme:product:add` e, depois de
 * recarregar o carrinho lateral, `theme:product:added` (que abre o carrinho lateral). Este
 * arquivo abre o popup no primeiro evento e segura a abertura do carrinho lateral no segundo,
 * sem mexer no theme.js.
 */
(function () {
  if (customElements.get('order-bump')) return;

  const MOBILE = window.matchMedia('(max-width: 749px)');
  const SUPPRESS_MS = 10000;

  const escapeHtml = (text) =>
    String(text ?? '').replace(/[&<>"']/g, (c) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));

  const formatMoney = (cents) => {
    if (window.theme && typeof window.theme.formatMoney === 'function') {
      return window.theme.formatMoney(cents, window.theme.moneyFormat);
    }
    return (cents / 100).toLocaleString('pt-BR', {style: 'currency', currency: 'BRL'});
  };

  const ICONS = {
    shipping:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6h11v9H3zM14 9h4l3 3v3h-7z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><circle cx="7" cy="17" r="1.8" fill="currentColor"/><circle cx="17.5" cy="17" r="1.8" fill="currentColor"/></svg>',
    gift: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 10h16v3H4zM5.5 13h13v7h-13zM12 10v10M12 10c-1.5-3-5-3.5-5-1.2C7 10 12 10 12 10zm0 0c1.5-3 5-3.5 5-1.2C17 10 12 10 12 10z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg>',
  };

  class OrderBump extends HTMLElement {
    connectedCallback() {
      this.config = JSON.parse(this.querySelector('[data-ob-config]').textContent);
      this.panel = this.querySelector('[data-ob-panel]');
      this.addedList = this.querySelector('[data-ob-added]');
      this.upsell = this.querySelector('[data-ob-upsell]');
      this.track = this.querySelector('[data-ob-track]');
      this.isOpen = false;
      this.suppressUntil = 0;

      this.onProductAdd = this.onProductAdd.bind(this);
      this.onProductAdded = this.onProductAdded.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      document.addEventListener('theme:product:add', this.onProductAdd);
      // Captura em window: roda antes do listener do carrinho lateral (em document).
      window.addEventListener('theme:product:added', this.onProductAdded, true);

      this.querySelectorAll('[data-ob-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.querySelector('[data-ob-view-cart]').addEventListener('click', (event) => this.viewCart(event));
      this.querySelector('[data-ob-prev]').addEventListener('click', () => this.slide(-1));
      this.querySelector('[data-ob-next]').addEventListener('click', () => this.slide(1));
      this.track.addEventListener('click', (event) => {
        const button = event.target.closest('[data-ob-add]');
        if (button) this.addUpsell(button.closest('[data-ob-card]'), button);
      });
      this.track.addEventListener('change', (event) => {
        if (event.target.matches('select[data-ob-variant]')) this.updateCardPrice(event.target);
      });
      this.track.addEventListener('scroll', () => this.updateArrows(), {passive: true});
      this.bindDrag();
    }

    disconnectedCallback() {
      document.removeEventListener('theme:product:add', this.onProductAdd);
      window.removeEventListener('theme:product:added', this.onProductAdded, true);
    }

    get cartDrawer() {
      return document.querySelector('cart-drawer');
    }

    /* ---------- Integração com o tema ---------- */

    onProductAdd(event) {
      const drawer = this.cartDrawer;
      const target = event.target;
      // Adição feita de dentro do carrinho lateral aberto: deixa o tema seguir normal.
      if (drawer && (drawer.cartDrawerIsOpen || (target instanceof Element && target.closest('cart-drawer')))) return;
      if (window.theme?.settings?.cartType === 'page') return;

      const response = event.detail?.response;
      if (!response) return;
      const items = (Array.isArray(response.items) ? response.items : [response]).filter((item) => item && item.variant_id);
      if (!items.length) return;

      this.suppressUntil = Date.now() + SUPPRESS_MS;
      this.open(items);
    }

    onProductAdded() {
      if (!this.isOpen && Date.now() > this.suppressUntil) return;
      const drawer = this.cartDrawer;
      if (!drawer || drawer.cartDrawerIsOpen) return;
      // O carrinho lateral só abre se `cartDrawerIsOpen` for falso: marca como aberto durante
      // este evento (síncrono) e devolve o estado logo depois.
      drawer.cartDrawerIsOpen = true;
      setTimeout(() => {
        if (!drawer.classList.contains('is-open')) drawer.cartDrawerIsOpen = false;
      });
    }

    refreshThemeCart() {
      this.suppressUntil = Date.now() + SUPPRESS_MS;
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
    }

    /* ---------- Abrir / fechar ---------- */

    open(items) {
      this.lastFocus = document.activeElement;
      this.addedList.innerHTML = '';
      items.forEach((item) => this.renderAddedItem(item));
      this.track.innerHTML = '';
      this.upsell.hidden = true;
      this.setTotals(null);

      this.hidden = false;
      this.panel.style.transform = '';
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        this.panel.focus({preventScroll: true});
      });
      document.documentElement.classList.add('order-bump-lock');
      document.addEventListener('keydown', this.onKeydown);
      this.isOpen = true;

      this.fetchCart();
      this.loadUpsell(items[0]);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('order-bump-lock');
      document.removeEventListener('keydown', this.onKeydown);
      setTimeout(() => {
        if (!this.isOpen) this.hidden = true;
      }, 300);
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') this.lastFocus.focus({preventScroll: true});
    }

    viewCart(event) {
      const drawer = this.cartDrawer;
      if (!drawer) return; // segue o link para a página do carrinho
      event.preventDefault();
      this.close();
      drawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show'));
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.panel.querySelectorAll('a[href], button:not([disabled]), select, input:not([type="hidden"])')).filter(
        (el) => el.offsetParent !== null
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* Arrastar para baixo fecha (celular). */
    bindDrag() {
      let startY = null;
      let delta = 0;
      this.querySelectorAll('[data-ob-drag]').forEach((handle) => {
        handle.addEventListener(
          'touchstart',
          (event) => {
            if (!MOBILE.matches || event.target.closest('button')) return;
            startY = event.touches[0].clientY;
            delta = 0;
            this.panel.style.transition = 'none';
          },
          {passive: true}
        );
      });
      this.panel.addEventListener(
        'touchmove',
        (event) => {
          if (startY === null) return;
          delta = Math.max(0, event.touches[0].clientY - startY);
          this.panel.style.transform = `translateY(${delta}px)`;
        },
        {passive: true}
      );
      this.panel.addEventListener('touchend', () => {
        if (startY === null) return;
        startY = null;
        this.panel.style.transition = '';
        this.panel.style.transform = '';
        if (delta > 90) this.close();
      });
    }

    /* ---------- Conteúdo ---------- */

    renderAddedItem(item) {
      const variant = item.variant_title && !/default title/i.test(item.variant_title) ? item.variant_title : '';
      const image = item.featured_image?.url || item.image;
      const imageUrl = image && /^(https?:)?\/\//.test(image) ? `${image}${image.includes('?') ? '&' : '?'}width=160` : image || '';
      const li = document.createElement('li');
      li.className = 'order-bump__item';
      li.innerHTML = `
        <div class="order-bump__item-media">${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="" width="64" height="64">` : ''}</div>
        <div class="order-bump__item-info">
          <p class="order-bump__item-title">${escapeHtml(item.product_title || item.title)}</p>
          ${variant ? `<p class="order-bump__item-variant">${escapeHtml(variant)}</p>` : ''}
          <p class="order-bump__item-price">${item.quantity} x ${formatMoney(item.final_price ?? item.price)}</p>
        </div>`;
      this.addedList.appendChild(li);
    }

    fetchCart() {
      return fetch(`${this.config.root.replace(/\/$/, '')}/cart.js`, {headers: {Accept: 'application/json'}})
        .then((response) => response.json())
        .then((cart) => {
          this.setTotals(cart);
          return cart;
        })
        .catch(() => this.setTotals(null));
    }

    setTotals(cart) {
      const total = this.querySelector('[data-ob-total]');
      const count = this.querySelector('[data-ob-count]');
      if (!cart) {
        total.textContent = '';
        count.textContent = '';
        total.classList.add('is-loading');
        return;
      }
      total.classList.remove('is-loading');
      total.textContent = formatMoney(cart.total_price);
      count.textContent = `(${cart.item_count} ${cart.item_count === 1 ? 'item' : 'itens'})`;
      this.renderProgress(cart.total_price);
    }

    renderProgress(total) {
      const holder = this.querySelector('[data-ob-progress]');
      const goals = (this.config.goals || []).filter((goal) => goal.value > 0).sort((a, b) => a.value - b.value);
      if (!holder || !this.config.progress || !goals.length) return;

      const max = goals[goals.length - 1].value;
      const next = goals.find((goal) => total < goal.value);
      const reached = goals.filter((goal) => total >= goal.value);
      let text;
      if (next) {
        const missing = `<strong>${formatMoney(next.value - total)}</strong>`;
        text = escapeHtml(next.message).replace('||amount||', missing);
      } else {
        text = `<strong>${escapeHtml(goals[goals.length - 1].success)}</strong>`;
      }
      if (next && reached.length) {
        text = `<span class="order-bump__progress-done">${ICONS[reached[reached.length - 1].type] || ''}${escapeHtml(
          reached[reached.length - 1].success
        )}</span>${text}`;
      }

      holder.hidden = false;
      holder.classList.toggle('is-complete', !next);
      this.querySelector('[data-ob-progress-text]').innerHTML = text;
      this.querySelector('[data-ob-progress-fill]').style.width = `${Math.min(100, (total / max) * 100)}%`;
      this.querySelector('[data-ob-progress-marks]').innerHTML = goals
        .map(
          (goal) =>
            `<span class="order-bump__mark${total >= goal.value ? ' is-reached' : ''}" style="left:${(goal.value / max) * 100}%" title="${escapeHtml(
              formatMoney(goal.value)
            )}">${ICONS[goal.type] || ''}</span>`
        )
        .join('');
    }

    loadUpsell(item) {
      const root = this.config.root.replace(/\/$/, '');
      const sectionId = this.config.sectionId;
      const seen = new Set([String(item.product_id)]);
      const token = (this.loadToken = Symbol('load'));

      const parse = (html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return Array.from(doc.querySelectorAll('[data-ob-card]')).filter((card) => {
          const id = card.dataset.productId;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });
      };

      const getSection = (url) =>
        fetch(url)
          .then((response) => (response.ok ? response.text() : ''))
          .catch(() => '');

      getSection(`${root}/products/${encodeURIComponent(item.handle)}?section_id=${sectionId}`)
        .then((html) => {
          const cards = parse(html);
          if (cards.length >= this.config.limit || !this.config.useRecommendations) return cards;
          return getSection(
            `${root}/recommendations/products?product_id=${item.product_id}&limit=${this.config.limit + 4}&section_id=${sectionId}`
          ).then((recHtml) => cards.concat(parse(recHtml)));
        })
        .then((cards) => {
          if (token !== this.loadToken || !this.isOpen) return;
          cards.slice(0, this.config.limit).forEach((card) => this.track.appendChild(document.importNode(card, true)));
          this.upsell.hidden = this.track.children.length === 0;
          this.track.scrollLeft = 0;
          this.updateArrows();
        });
    }

    updateCardPrice(select) {
      const option = select.selectedOptions[0];
      const card = select.closest('[data-ob-card]');
      const price = parseInt(option.dataset.price, 10);
      const compare = parseInt(option.dataset.compare, 10);
      card.querySelector('[data-ob-price]').innerHTML =
        (compare > price ? `<s>${formatMoney(compare)}</s>` : '') + `<span>${formatMoney(price)}</span>`;

      // Trocou o sabor depois de adicionar: libera o botão para adicionar o outro sabor.
      const button = card.querySelector('[data-ob-add]');
      if (card.classList.contains('is-added') && !button.classList.contains('is-loading')) {
        card.classList.remove('is-added');
        button.classList.remove('is-added');
        button.disabled = false;
        button.querySelector('[data-ob-add-label]').textContent = `+ ${this.config.strings.add}`;
      }
    }

    addUpsell(card, button) {
      const variantId = card.querySelector('[data-ob-variant]')?.value;
      const error = card.querySelector('[data-ob-error]');
      if (!variantId || button.disabled) return;

      button.disabled = true;
      button.classList.add('is-loading');
      error.hidden = true;

      fetch(`${this.config.root.replace(/\/$/, '')}/cart/add.js`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({items: [{id: Number(variantId), quantity: 1}]}),
      })
        .then((response) => response.json())
        .then((response) => {
          button.classList.remove('is-loading');
          if (response.status && response.status !== 200) {
            button.disabled = false;
            error.textContent = response.description || response.message || this.config.strings.error;
            error.hidden = false;
            return;
          }
          (response.items || [response]).forEach((item) => this.renderAddedItem(item));
          button.classList.add('is-added');
          button.querySelector('[data-ob-add-label]').textContent = `✓ ${this.config.strings.added}`;
          card.classList.add('is-added');
          this.fetchCart();
          this.refreshThemeCart();
        })
        .catch(() => {
          button.classList.remove('is-loading');
          button.disabled = false;
          error.textContent = this.config.strings.error;
          error.hidden = false;
        });
    }

    /* ---------- Carrossel ---------- */

    slide(direction) {
      const card = this.track.querySelector('[data-ob-card]');
      const step = card ? card.getBoundingClientRect().width + 12 : this.track.clientWidth;
      this.track.scrollBy({left: direction * step * (MOBILE.matches ? 1 : 2), behavior: 'smooth'});
    }

    updateArrows() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      this.querySelector('[data-ob-prev]').disabled = this.track.scrollLeft <= 2;
      this.querySelector('[data-ob-next]').disabled = this.track.scrollLeft >= max;
      this.upsell.classList.toggle('has-scroll', max > 0);
    }
  }

  customElements.define('order-bump', OrderBump);
})();
